import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { adminPlayerStatsService, adminPlayerMessagingService } from '../../../services/adminUserManagementService';
import { playerProfileService } from '../../../services/playerMasterService';
import { supabase } from '../../../lib/supabase';

const AdminPlayerStatsManager = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('statistics');
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [messages, setMessages] = useState([]);
  const [playerReplies, setPlayerReplies] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageThread, setMessageThread] = useState([]);

  const [statsForm, setStatsForm] = useState({
    playerId: '',
    totalViews: 0,
    scoutViews: 0,
    clubViews: 0,
    agentViews: 0,
    activeClubInterest: 0,
    totalScoutFeedback: 0,
    avgScoutRating: 0,
    viewsLast7Days: 0,
    viewsLast30Days: 0,
    topInterestedPositions: '',
    topInterestedLeagues: ''
  });

  const [messageForm, setMessageForm] = useState({
    toUserId: '',
    toPlayerId: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const playersRes = await playerProfileService?.getAllForAdmin();
      setPlayers(playersRes?.data || []);

      const { data: usersData } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, email, role, assigned_profile_id')
        ?.eq('is_confirmed', true)
        ?.order('full_name', { ascending: true });
      setUsers(usersData || []);

      if (activeTab === 'messages') {
        const sentMessages = await adminPlayerMessagingService?.getAdminSentMessages();
        setMessages(sentMessages?.data || []);
      } else if (activeTab === 'replies') {
        const replies = await adminPlayerMessagingService?.getPlayerReplies();
        setPlayerReplies(replies?.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadPlayerStats = async (playerId) => {
    if (!playerId) return;
    try {
      const statsRes = await adminPlayerStatsService?.getPlayerStats(playerId);
      if (statsRes?.data) {
        setStatsForm({
          playerId: playerId,
          totalViews: statsRes?.data?.totalViews || 0,
          scoutViews: statsRes?.data?.scoutViews || 0,
          clubViews: statsRes?.data?.clubViews || 0,
          agentViews: statsRes?.data?.agentViews || 0,
          activeClubInterest: statsRes?.data?.activeClubInterest || 0,
          totalScoutFeedback: statsRes?.data?.totalScoutFeedback || 0,
          avgScoutRating: statsRes?.data?.avgScoutRating || 0,
          viewsLast7Days: statsRes?.data?.viewsLast7Days || 0,
          viewsLast30Days: statsRes?.data?.viewsLast30Days || 0,
          topInterestedPositions: statsRes?.data?.topInterestedPositions?.join(', ') || '',
          topInterestedLeagues: statsRes?.data?.topInterestedLeagues?.join(', ') || ''
        });
      }
    } catch (error) {
      console.error('Error loading player stats:', error);
    }
  };

  const handleUpdateStats = async () => {
    if (!statsForm?.playerId) {
      alert('Please select a player');
      return;
    }

    try {
      setLoading(true);
      const statsData = {
        totalViews: parseInt(statsForm?.totalViews) || 0,
        scoutViews: parseInt(statsForm?.scoutViews) || 0,
        clubViews: parseInt(statsForm?.clubViews) || 0,
        agentViews: parseInt(statsForm?.agentViews) || 0,
        activeClubInterest: parseInt(statsForm?.activeClubInterest) || 0,
        totalScoutFeedback: parseInt(statsForm?.totalScoutFeedback) || 0,
        avgScoutRating: parseFloat(statsForm?.avgScoutRating) || 0,
        viewsLast7Days: parseInt(statsForm?.viewsLast7Days) || 0,
        viewsLast30Days: parseInt(statsForm?.viewsLast30Days) || 0,
        topInterestedPositions: statsForm?.topInterestedPositions?.split(',')?.map(p => p?.trim())?.filter(Boolean) || [],
        topInterestedLeagues: statsForm?.topInterestedLeagues?.split(',')?.map(l => l?.trim())?.filter(Boolean) || []
      };

      await adminPlayerStatsService?.updatePlayerStats(statsForm?.playerId, statsData);
      alert('Player statistics updated successfully!');
      setStatsForm({
        playerId: '',
        totalViews: 0,
        scoutViews: 0,
        clubViews: 0,
        agentViews: 0,
        activeClubInterest: 0,
        totalScoutFeedback: 0,
        avgScoutRating: 0,
        viewsLast7Days: 0,
        viewsLast30Days: 0,
        topInterestedPositions: '',
        topInterestedLeagues: ''
      });
      setSelectedPlayerId('');
    } catch (error) {
      console.error('Error updating stats:', error);
      alert('Failed to update statistics: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageForm?.toUserId || !messageForm?.subject || !messageForm?.message) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await adminPlayerMessagingService?.sendMessageToPlayer(messageForm);
      alert('Message sent successfully!');
      setMessageForm({ toUserId: '', toPlayerId: '', subject: '', message: '' });
      loadData();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewThread = async (message) => {
    setSelectedMessage(message);
    try {
      const threadRes = await adminPlayerMessagingService?.getMessageThread(message?.id);
      setMessageThread(threadRes?.data || []);
    } catch (error) {
      console.error('Error loading thread:', error);
    }
  };

  const handleUserChange = (userId) => {
    setMessageForm({ ...messageForm, toUserId: userId });
    const user = users?.find(u => u?.id === userId);
    if (user?.assigned_profile_id) {
      setMessageForm({ ...messageForm, toUserId: userId, toPlayerId: user?.assigned_profile_id });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">Player Statistics & Messaging Manager</h2>
          <Button variant="ghost" size="icon" iconName="X" onClick={onClose} />
        </div>

        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('statistics')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'statistics' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="BarChart" size={20} className="inline mr-2" />
            Update Statistics
          </button>
          <button
            onClick={() => setActiveTab('messages')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'messages' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Send" size={20} className="inline mr-2" />
            Send Messages
          </button>
          <button
            onClick={() => setActiveTab('replies')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'replies' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="MessageSquare" size={20} className="inline mr-2" />
            View Player Replies
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'statistics' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Select Player</label>
                  <Select
                    value={selectedPlayerId}
                    options={[
                      { value: '', label: 'Choose a player...' },
                      ...players?.map((player) => ({
                        value: player?.id,
                        label: `${player?.fullName} - ${player?.primaryPosition}`
                      }))
                    ]}
                    onChange={(val) => {
                      const playerId = val;
                      setSelectedPlayerId(playerId);
                      setStatsForm({ ...statsForm, playerId });
                      handleLoadPlayerStats(playerId);
                    }}
                  />
                </div>
              </div>

              {selectedPlayerId && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Total Views</label>
                    <Input
                      type="number"
                      value={statsForm?.totalViews}
                      onChange={(e) => setStatsForm({ ...statsForm, totalViews: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Scout Views</label>
                    <Input
                      type="number"
                      value={statsForm?.scoutViews}
                      onChange={(e) => setStatsForm({ ...statsForm, scoutViews: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Club Views</label>
                    <Input
                      type="number"
                      value={statsForm?.clubViews}
                      onChange={(e) => setStatsForm({ ...statsForm, clubViews: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Agent Views</label>
                    <Input
                      type="number"
                      value={statsForm?.agentViews}
                      onChange={(e) => setStatsForm({ ...statsForm, agentViews: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Active Club Interest</label>
                    <Input
                      type="number"
                      value={statsForm?.activeClubInterest}
                      onChange={(e) => setStatsForm({ ...statsForm, activeClubInterest: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Scout Feedback Count</label>
                    <Input
                      type="number"
                      value={statsForm?.totalScoutFeedback}
                      onChange={(e) => setStatsForm({ ...statsForm, totalScoutFeedback: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Avg Scout Rating (1-5)</label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      value={statsForm?.avgScoutRating}
                      onChange={(e) => setStatsForm({ ...statsForm, avgScoutRating: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Views Last 7 Days</label>
                    <Input
                      type="number"
                      value={statsForm?.viewsLast7Days}
                      onChange={(e) => setStatsForm({ ...statsForm, viewsLast7Days: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Views Last 30 Days</label>
                    <Input
                      type="number"
                      value={statsForm?.viewsLast30Days}
                      onChange={(e) => setStatsForm({ ...statsForm, viewsLast30Days: e?.target?.value })}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Top Interested Positions (comma-separated)
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., Striker, Midfielder, Winger"
                      value={statsForm?.topInterestedPositions}
                      onChange={(e) => setStatsForm({ ...statsForm, topInterestedPositions: e?.target?.value })}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Top Interested Leagues (comma-separated)
                    </label>
                    <Input
                      type="text"
                      placeholder="e.g., Premier League, La Liga, Serie A"
                      value={statsForm?.topInterestedLeagues}
                      onChange={(e) => setStatsForm({ ...statsForm, topInterestedLeagues: e?.target?.value })}
                    />
                  </div>
                </div>
              )}

              {selectedPlayerId && (
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => {
                    setSelectedPlayerId('');
                    setStatsForm({
                      playerId: '',
                      totalViews: 0,
                      scoutViews: 0,
                      clubViews: 0,
                      agentViews: 0,
                      activeClubInterest: 0,
                      totalScoutFeedback: 0,
                      avgScoutRating: 0,
                      viewsLast7Days: 0,
                      viewsLast30Days: 0,
                      topInterestedPositions: '',
                      topInterestedLeagues: ''
                    });
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateStats} disabled={loading}>
                    {loading ? 'Updating...' : 'Update Statistics'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'messages' && (
            <div className="space-y-6">
              <div className="bg-muted/30 rounded-lg p-6 space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Send Message to Player</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Select Player</label>
                    <Select
                      value={messageForm?.toUserId}
                      options={[
                        { value: '', label: 'Choose a player...' },
                        ...users?.map((u) => ({
                          value: u?.id,
                          label: `${u?.full_name} (${u?.email})`
                        }))
                      ]}
                      onChange={(val) => handleUserChange(val)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Subject</label>
                    <Input
                      type="text"
                      placeholder="Message subject"
                      value={messageForm?.subject}
                      onChange={(e) => setMessageForm({ ...messageForm, subject: e?.target?.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Message</label>
                    <textarea
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground min-h-[120px]"
                      placeholder="Type your message here..."
                      value={messageForm?.message}
                      onChange={(e) => setMessageForm({ ...messageForm, message: e?.target?.value })}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSendMessage} disabled={loading}>
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Sent Messages</h3>
                <div className="space-y-3">
                  {messages?.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No messages sent yet</p>
                  ) : (
                    messages?.map((msg) => (
                      <div key={msg?.id} className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-foreground">{msg?.subject}</h4>
                              {msg?.hasReplies && (
                                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                  Has Replies
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              To: {msg?.toUser?.full_name || msg?.toUser?.email}
                            </p>
                            <p className="text-sm text-foreground line-clamp-2">{msg?.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(msg?.createdAt)?.toLocaleString()}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewThread(msg)}
                          >
                            View Thread
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'replies' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Player Replies</h3>
              {playerReplies?.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No player replies yet</p>
              ) : (
                <div className="space-y-3">
                  {playerReplies?.map((reply) => (
                    <div key={reply?.id} className="bg-muted/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Icon name="User" size={16} className="text-primary" />
                            <span className="font-semibold text-foreground">
                              {reply?.fromAdmin?.full_name || reply?.fromAdmin?.email}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              replied to: {reply?.replyTo?.subject}
                            </span>
                          </div>
                          <h4 className="font-semibold text-foreground mb-1">{reply?.subject}</h4>
                          <p className="text-sm text-foreground">{reply?.message}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(reply?.createdAt)?.toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewThread({ id: reply?.replyToMessageId })}
                        >
                          View Full Thread
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {selectedMessage && messageThread?.length > 0 && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg border border-border w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Message Thread</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="X"
                  onClick={() => {
                    setSelectedMessage(null);
                    setMessageThread([]);
                  }}
                />
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messageThread?.map((msg, index) => (
                  <div
                    key={msg?.id}
                    className={`p-4 rounded-lg ${
                      msg?.senderType === 'admin' ?'bg-primary/10 ml-0 mr-8' :'bg-accent/10 ml-8 mr-0'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon
                        name={msg?.senderType === 'admin' ? 'Shield' : 'User'}
                        size={16}
                        className={msg?.senderType === 'admin' ? 'text-primary' : 'text-accent'}
                      />
                      <span className="font-semibold text-foreground">
                        {msg?.senderType === 'admin' ? 'Admin' : 'Player'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(msg?.createdAt)?.toLocaleString()}
                      </span>
                    </div>
                    <h4 className="font-semibold text-foreground mb-1">{msg?.subject}</h4>
                    <p className="text-sm text-foreground">{msg?.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPlayerStatsManager;
